import { mimeTypes } from '@/shared/constants';
import type { DriveFile, Revision } from '@/shared/DriveTypes';
import { ArrowDropDown, Schedule, Timeline } from '@mui/icons-material';
import { Avatar, Menu, MenuItem, Skeleton } from '@mui/material';
import cx from 'classnames';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import Icon from '~/elements/Icon/Icon';
import { darkTheme, lightTheme } from '~/theme.css';
import { listRevisions } from '~/utils/DriveAPI';
import { getTitle, isMarkdownLinkTitle } from '~/utils/isMarkdownLink';
import { log } from '~/utils/logger';
import { useTheme } from '~/utils/store';
import { isADocument, isAFolder, ls, resizePhoto, toTitleCase } from '~/utils/utils';
import * as styles from './RecentlyChanged.css';

const profileSize = 32 * window.devicePixelRatio;
const INITIAL_ITEMS = 10;
const ITEMS_INCREMENT = 10;
const SKELETON_COUNT = 4;

type Props = {
  files: DriveFile[];
  wiki: DriveFile;
  isLoading?: boolean;
};

const SkeletonCard = () => (
  <div className={styles.card}>
    <Skeleton variant="circular" width={32} height={32} />
    <div className={styles.cardContent}>
      <Skeleton variant="text" width="70%" height={20} />
      <Skeleton variant="text" width="50%" height={16} />
    </div>
  </div>
);

type FileWithRevisions = {
  file: DriveFile;
  revisions?: Revision[];
};

export const RecentlyChanged = ({ files, wiki, isLoading }: Props) => {
  const [viewMode, setViewMode] = useState<'timeline' | 'grid'>(
    () => ls.get<'timeline' | 'grid'>('recentlyChangedViewMode') ?? 'grid'
  );
  const [displayedCount, setDisplayedCount] = useState(INITIAL_ITEMS);
  const [revisionData, setRevisionData] = useState<Map<string, Revision[]>>(new Map());
  const menu = usePopupState({ variant: 'popover', popupId: 'view-mode-menu' });
  const theme = useTheme();

  const allRecentFiles = files
    .filter((file) => !isAFolder(file) && file.mimeType !== mimeTypes.shortcut)
    .sort((a, b) => new Date(b.modifiedTime).getTime() - new Date(a.modifiedTime).getTime());

  const recentFiles = allRecentFiles.slice(0, displayedCount);
  const hasMore = allRecentFiles.length > displayedCount;

  useEffect(() => {
    if (viewMode !== 'timeline' || recentFiles.length === 0) return;

    const abortController = new AbortController();

    const fetchRevisions = async () => {
      const filesToFetch = recentFiles.filter((file) => !revisionData.has(file.id));
      if (filesToFetch.length === 0) return;

      try {
        await Promise.all(
          filesToFetch.map(async (file) => {
            try {
              const revisions = await listRevisions({ fileId: file.id, pageSize: 5 });
              if (!abortController.signal.aborted) {
                setRevisionData((prev) => new Map(prev).set(file.id, revisions));
              }
            } catch (error) {
              if (!abortController.signal.aborted) {
                log.error(`Failed to fetch revisions for ${file.name}`, error);
              }
            }
          })
        );
      } catch (error) {
        if (!abortController.signal.aborted) {
          log.error('Failed to fetch revisions', error);
        }
      }
    };

    void fetchRevisions();

    return () => {
      abortController.abort();
    };
  }, [viewMode, recentFiles, revisionData]);

  const filesWithRevisions: FileWithRevisions[] = recentFiles.map((file) => {
    const revisions = revisionData.get(file.id);
    return revisions ? { file, revisions } : { file };
  });

  if (!isLoading && recentFiles.length === 0) return null;

  const groupedFiles =
    viewMode === 'timeline'
      ? filesWithRevisions.reduce<Record<string, FileWithRevisions[]>>((acc, item) => {
          const itemDate = new Date(item.file.modifiedTime);
          const today = new Date();
          const isToday =
            itemDate.getDate() === today.getDate() &&
            itemDate.getMonth() === today.getMonth() &&
            itemDate.getFullYear() === today.getFullYear();

          const date = isToday
            ? 'Today'
            : itemDate.toLocaleDateString(undefined, {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
              });
          if (!acc[date]) acc[date] = [];
          acc[date].push(item);
          return acc;
        }, {})
      : {};

  return (
    <section className={styles.container} data-testid="recently-changed">
      <div className={styles.header}>
        <button type="button" className={styles.headerButton} {...bindTrigger(menu)}>
          {viewMode === 'timeline' ? (
            <Timeline className={styles.headerIcon} style={{ fontSize: 18 }} />
          ) : (
            <Schedule className={styles.headerIcon} style={{ fontSize: 18 }} />
          )}
          {viewMode === 'timeline' ? 'Timeline' : 'Recent updates'}
          <ArrowDropDown style={{ fontSize: 18, opacity: 0.5 }} />
        </button>
        <Menu
          {...bindMenu(menu)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          className={cx(styles.menu, {
            [darkTheme]: theme === 'dark',
            [lightTheme]: theme === 'light',
          })}
        >
          <MenuItem
            className={styles.menuItem}
            onClick={() => {
              setViewMode('grid');
              ls.set('recentlyChangedViewMode', 'grid');
              menu.close();
            }}
            selected={viewMode === 'grid'}
          >
            <Schedule className={styles.menuIcon} /> Recent updates
          </MenuItem>
          <MenuItem
            className={styles.menuItem}
            onClick={() => {
              setViewMode('timeline');
              ls.set('recentlyChangedViewMode', 'timeline');
              menu.close();
            }}
            selected={viewMode === 'timeline'}
          >
            <Timeline className={styles.menuIcon} /> Timeline
          </MenuItem>
        </Menu>
      </div>

      <div className={viewMode === 'timeline' ? styles.timeline : styles.grid}>
        {isLoading
          ? Array.from({ length: SKELETON_COUNT }, (_, i) =>
              viewMode === 'timeline' ? (
                <div key={i} className={styles.timelineItem}>
                  <div className={styles.timelineNode} />
                  <SkeletonCard />
                </div>
              ) : (
                <SkeletonCard key={i} />
              )
            )
          : viewMode === 'timeline'
            ? Object.entries(groupedFiles).map(([date, items]) => (
                <div key={date} className={styles.dateGroup}>
                  <div className={styles.dateHeader}>{date}</div>
                  {items.map(({ file, revisions }) => {
                    const lastModifier = file.lastModifyingUser;
                    const title = getTitle(file);
                    const time = new Date(file.modifiedTime).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    });
                    const externalLink = isADocument(file) ? isMarkdownLinkTitle(file.name) : undefined;
                    const href = externalLink?.url ?? `/app/page/${wiki.id}/${file.id}`;

                    const fileDate = new Date(file.modifiedTime);
                    const revisionCount =
                      revisions?.filter((r) => {
                        const revDate = new Date(r.modifiedTime);
                        return (
                          revDate.getDate() === fileDate.getDate() &&
                          revDate.getMonth() === fileDate.getMonth() &&
                          revDate.getFullYear() === fileDate.getFullYear()
                        );
                      }).length || 0;

                    return (
                      <div key={file.id} className={styles.timelineItem}>
                        <div className={styles.timelineNode} />
                        <Link
                          to={href}
                          className={styles.card}
                          data-testid="recently-changed-item"
                          {...(externalLink?.url && { target: '_blank', rel: 'noopener noreferrer' })}
                        >
                          <Avatar
                            className={styles.avatar}
                            slotProps={{ img: { referrerPolicy: 'no-referrer' } }}
                            alt={lastModifier?.displayName ?? ''}
                            src={resizePhoto(lastModifier?.photoLink, profileSize)}
                          >
                            {lastModifier?.displayName?.[0]}
                          </Avatar>
                          <div className={styles.cardContent}>
                            <div className={styles.cardTitle}>{title}</div>
                            <div className={styles.cardMeta}>
                              <Icon file={file} className={styles.icon} externalLink={externalLink} />
                              <span>{time}</span>
                              {lastModifier && <span>• {toTitleCase(lastModifier.displayName)}</span>}
                              {revisionCount > 1 && (
                                <span className={styles.revisionCount}>• {revisionCount} edits</span>
                              )}
                            </div>
                          </div>
                        </Link>
                      </div>
                    );
                  })}
                </div>
              ))
            : recentFiles.map((file) => {
                const lastModifier = file.lastModifyingUser;
                const title = getTitle(file);
                const time = new Date(file.modifiedTime).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                });
                const externalLink = isADocument(file) ? isMarkdownLinkTitle(file.name) : undefined;
                const href = externalLink?.url ?? `/app/page/${wiki.id}/${file.id}`;

                return (
                  <Link
                    key={file.id}
                    to={href}
                    className={styles.card}
                    data-testid="recently-changed-item"
                    {...(externalLink?.url && { target: '_blank', rel: 'noopener noreferrer' })}
                  >
                    <Avatar
                      className={styles.avatar}
                      slotProps={{ img: { referrerPolicy: 'no-referrer' } }}
                      alt={lastModifier?.displayName ?? ''}
                      src={resizePhoto(lastModifier?.photoLink, profileSize)}
                    >
                      {lastModifier?.displayName?.[0]}
                    </Avatar>
                    <div className={styles.cardContent}>
                      <div className={styles.cardTitle}>{title}</div>
                      <div className={styles.cardMeta}>
                        <Icon file={file} className={styles.icon} externalLink={externalLink} />
                        <span>{time}</span>
                        {lastModifier && <span>• {toTitleCase(lastModifier.displayName)}</span>}
                      </div>
                    </div>
                  </Link>
                );
              })}
      </div>
      {hasMore && !isLoading && (
        <button
          type="button"
          className={styles.loadMoreButton}
          onClick={() => setDisplayedCount((prev) => prev + ITEMS_INCREMENT)}
        >
          Show more ({allRecentFiles.length - displayedCount} remaining)
        </button>
      )}
    </section>
  );
};
